import type { SmartSuiteRecordListConfig } from "@smartsuite/automation-conditions";
import { smartSuiteRecordListConfig } from "@smartsuite/automation-conditions";
import type { ActionConfig } from "@smartsuite/automation-engine-api";
import { INPUT_APPLICATION_ID } from "./constants";
import { findInputAndPick, pickValueConfigString } from "./inputs";

const isSet = (value: string | undefined): value is string =>
  value !== undefined && value !== "";

/**
 * An identity of the records an action was handed: read from the record list
 * it consumes, falling back to the legacy input the producers still declare.
 *
 * The record list wins. The table and the solution are properties of the
 * records, so a consumer that also carries a stored input can only disagree
 * with the list by being stale.
 */
export const recordListIdentity = (
  action: ActionConfig | undefined,
  pick: (recordList: SmartSuiteRecordListConfig) => string | undefined,
  inputId: string,
): string | undefined => {
  const recordList = smartSuiteRecordListConfig(action);
  const fromRecordList =
    recordList === undefined ? undefined : pick(recordList);

  if (isSet(fromRecordList)) {
    return fromRecordList;
  }

  const fromInput =
    action === undefined
      ? undefined
      : findInputAndPick(action.inputs, inputId, pickValueConfigString);

  return isSet(fromInput) ? fromInput : undefined;
};

/**
 * The table an action works on: the table the records it was handed came from,
 * otherwise its own `application-id` input where it still declares one.
 */
export const actionApplicationId = (
  action: ActionConfig | undefined,
): string | undefined =>
  recordListIdentity(
    action,
    (recordList) => recordList.applicationId,
    INPUT_APPLICATION_ID,
  );
